import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../Logo.png';

import ApiService, { Credentials } from '../services/apiService';

import {
    containerStyle,
    formContainerStyle,
    headerStyle,
    titleStyle,
    formStyle,
    errorStyle,
    buttonStyle,
    inputStyle,
    footerTextStyle
} from '../styles/globalStyles';

interface ProductInfo {
    id: number;
    name: string;
    machine_ids: string[];
}

const Dashboard: React.FC = () => {
    const [products, setProducts] = useState<ProductInfo[]>( [] );
    const [selectedProductId, setSelectedProductId] = useState<number | null>( null );
    const [newMachineIds, setNewMachineIds] = useState<Record<number, string>>( {} );
    const [actionMessage, setActionMessage] = useState<Record<number, string>>( {} );
    const [actionError, setActionError] = useState<Record<number, string>>( {} );
    const [isLoading, setIsLoading] = useState( false );
    const [error, setError] = useState( '' );
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState( false );
    const [credentials, setCredentials] = useState< Credentials >( {
        email: '',
        password: ''
    } );

    const navigate = useNavigate();

    const handleLogoClick = () => {
        navigate( '/' );
    };

    const handleLogout = () => {
        localStorage.removeItem( 'rememberedLogin' );
        navigate( '/' );
    };

    const apiService = useMemo( () => {
        if( ! credentials.email || ! credentials.password )
            return null;

        return new ApiService( credentials );
    }, [ credentials ] );

    useEffect( () => {
        const storedLogin = localStorage.getItem( 'rememberedLogin' );

        if( ! storedLogin )
            return;

        try{
            const parsed = JSON.parse( storedLogin );
            setCredentials( {
                email: parsed.email || '',
                password: parsed.password || ''
            } );
        }catch{
            setCredentials( { email: '', password: '' } );
        }
    }, [] );

    useEffect( () => {
        const fetchProducts = async () => {
            if( ! apiService )
                return;

            setIsLoading( true );
            setError( '' );

            try{
                const data = await apiService.request<ProductInfo[]>( 'GET', '/api/v1/products/' );

                const productList = Array.isArray(data) ? data : [];
                setProducts(productList);
                if (productList.length > 0) {
                    setSelectedProductId(productList[0].id);
                }
            }catch( err ){
                setError( err instanceof Error ? err.message : 'Failed to load products. Please try again.' );
            }finally{
                setIsLoading( false );
            }
        };

        fetchProducts();
    }, [ apiService ] );

    const updateMachineId = ( productId: number, value: string ) => {
        setNewMachineIds( ( prev ) => ( { ...prev, [ productId ]: value } ) );
        setActionError( ( prev ) => ( { ...prev, [ productId ]: '' } ) );
        setActionMessage( ( prev ) => ( { ...prev, [ productId ]: '' } ) );
    };

    const requestMachineId = async ( productId: number ) => {
        const machineId = ( newMachineIds[ productId ] || '' ).trim();

        if( ! machineId ){
            setActionError( ( prev ) => ( { ...prev, [ productId ]: 'Please enter a machine ID.' } ) );
            return;
        }

        try{
            if( ! apiService )
                throw new Error('API service not initialized.');

            await apiService.request< boolean >( 'POST', '/api/v1/machine-ids/add', { body: {
                id: machineId,
                product_id: productId
            } } );

            setProducts( ( prev ) => prev.map( ( product ) => {
                if( product.id !== productId )
                    return product;

                if( product.machine_ids.includes( machineId ) )
                    return product;

                return {
                    ...product,
                    machine_ids: [ ...product.machine_ids, machineId ]
                };
            } ) );
            setNewMachineIds( ( prev ) => ( { ...prev, [ productId ]: '' } ) );
            setActionMessage( ( prev ) => ( { ...prev, [ productId ]: 'Machine ID added.' } ) );
        }catch( err ){
            setActionError( ( prev ) => ( { ...prev, [ productId ]: err instanceof Error ? err.message : 'Failed to request machine ID.' } ) );
        }
    };

    const requestDeleteMachineId = async ( productId: number, machineId: string ) => {
        try{
            if( ! apiService )
                throw new Error('API service not initialized.');

            await apiService.request<boolean>( 'POST', '/api/v1/machine-ids/delete', { body: {
                id: machineId,
                product_id: productId
            } } );

            setProducts( ( prev ) => prev.map( ( product ) => {
                if( product.id !== productId )
                    return product;

                return {
                    ...product,
                    machine_ids: product.machine_ids.filter( ( id ) => id !== machineId )
                };
            } ) );
            setActionMessage( ( prev ) => ( { ...prev, [ productId ]: 'Machine ID removed.' } ) );
        }catch( err ){
            setActionError( ( prev ) => ( { ...prev, [ productId ]: err instanceof Error ? err.message : 'Failed to request deletion.' } ) );
        }
    };

    return(
        <div style={{ ...containerStyle, flexDirection: 'row', alignItems: 'stretch', justifyContent: 'flex-start', height: '100vh', overflow: 'hidden' }}>
            <aside
                style={{
                    width: isSidebarCollapsed ? 72 : 240,
                    minHeight: '100%',
                    background: '#0f1012',
                    borderRight: '1px solid #222',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '20px 20px 56px 20px',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    bottom: 0
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    {! isSidebarCollapsed && (
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Dashboard</div>
                    )}
                    <button
                        type="button"
                        onClick={() => setIsSidebarCollapsed( ! isSidebarCollapsed )}
                        style={{
                            background: 'transparent',
                            color: '#b0b3b8',
                            border: '1px solid #222',
                            borderRadius: 6,
                            padding: '6px 10px',
                            cursor: 'pointer'
                        }}
                    >
                        {isSidebarCollapsed ? '>' : '<'}
                    </button>
                </div>
                <button
                    type="button"
                    onClick={handleLogoClick}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        marginBottom: 24
                    }}
                >
                    <img
                        src={logo}
                        alt="logo"
                        style={{ height: 40, width: 'auto' }}
                    />
                </button>
                {! isSidebarCollapsed && (
                    <div style={{ color: '#b0b3b8', fontSize: 14, marginBottom: 10 }}>Your Products</div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, overflowY: 'auto' }}>
                    {products.map( ( product ) => (
                        <button
                            key={`nav-${product.id}`}
                            type="button"
                            title={isSidebarCollapsed ? product.name : undefined}
                            onClick={() => {
                                setSelectedProductId( product.id );
                            }}
                            style={{
                                background: selectedProductId === product.id ? '#1f2937' : 'transparent',
                                color: '#fff',
                                border: selectedProductId === product.id ? '1px solid #22d3ee' : '1px solid #222',
                                borderRadius: 6,
                                padding: '8px 12px',
                                textAlign: 'left',
                                cursor: 'pointer'
                            }}
                        >
                            {isSidebarCollapsed ? product.name.charAt( 0 ) : product.name}
                        </button>
                    ) )}
                    {products.length === 0 && (
                        <div style={{ color: '#6b7280', fontSize: 13 }}>
                            No products yet
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleLogout}
                    title="LogOut"
                    style={{
                        position: 'absolute',
                        bottom: 16,
                        left: 16,
                        background: 'transparent',
                        color: '#ef4444',
                        border: 'none',
                        borderRadius: 6,
                        padding: 6,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <path d="M16 17l5-5-5-5" />
                        <path d="M21 12H9" />
                    </svg>
                </button>
            </aside>
            <main
                style={{
                    flex: 1,
                    padding: '32px 40px',
                    marginLeft: isSidebarCollapsed ? 72 : 240,
                    overflowY: 'auto'
                }}
            >
                <div style={{ ...formContainerStyle, maxWidth: '100%', width: '100%' }}>
                    <div style={headerStyle}>
                        <h1 style={titleStyle}>
                            {products.find( (product) => product.id === selectedProductId )?.name || 'Dashboard'}
                        </h1>
                    </div>
                    <div style={formStyle}>
                    {error && (
                        <div style={errorStyle}>
                            {error}
                        </div>
                    )}
                    {isLoading && (
                        <div style={{ ...footerTextStyle, marginTop: 0 }}>Loading products...</div>
                    )}
                    {! isLoading && ! error && products.length === 0 && (
                        <div style={{ ...footerTextStyle, marginTop: 0 }}>
                            You do not have any products yet. Purchase a product to get started.
                        </div>
                    )}
                    {! isLoading && ! error && products.length > 0 && selectedProductId === null && (
                        <div style={{ ...footerTextStyle, marginTop: 0 }}>
                            Select a product to view details.
                        </div>
                    )}
                    {! isLoading && ! error && products.map( ( product ) => (
                        product.id === selectedProductId ? (
                            <div
                                key={product.id}
                                style={{
                                    border: '1px solid #222',
                                    borderRadius: 8,
                                    padding: 16,
                                    background: '#141619'
                                }}
                            >
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ color: '#b0b3b8', marginBottom: 8 }}>Machine IDs</div>
                                    {product.machine_ids.length === 0 ? (
                                        <div style={{ color: '#b0b3b8' }}>No machine IDs registered.</div>
                                    ) : (
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {product.machine_ids.map( ( machineId ) => (
                                                <li
                                                    key={machineId}
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        background: '#111214',
                                                        padding: '10px 12px',
                                                        borderRadius: 6,
                                                        border: '1px solid #222'
                                                    }}
                                                >
                                                    <span style={{ color: '#fff' }}>{machineId}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => requestDeleteMachineId( product.id, machineId )}
                                                        style={{
                                                            background: 'transparent',
                                                            color: '#ef4444',
                                                            border: '1px solid #ef4444',
                                                            borderRadius: 6,
                                                            padding: '6px 12px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                </li>
                                            ) )}
                                        </ul>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <input
                                        placeholder="New Machine ID"
                                        style={inputStyle}
                                        value={newMachineIds[ product.id ] || ''}
                                        onChange={( event ) => updateMachineId( product.id, event.target.value )}
                                    />
                                    <button
                                        type="button"
                                        style={buttonStyle}
                                        onClick={() => requestMachineId( product.id )}
                                    >
                                        Add
                                    </button>
                                </div>
                                {( actionError[ product.id ] || actionMessage[ product.id ] ) && (
                                    <div style={{ ...footerTextStyle, color: actionError[ product.id ] ? '#ef4444' : '#22d3ee', marginTop: 12 }}>
                                        {actionError[ product.id ] || actionMessage[ product.id ]}
                                    </div>
                                )}
                            </div>
                        ) : null
                    ) )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
